import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import {
    Typography,
    Button,
    Stack,
    Card,
    CardContent,
    Box,
    IconButton,
    Chip,
    useTheme,
    useMediaQuery,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Tabs,
    Tab,
    Grid
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useCollection } from "../hooks/useCollection";
import { useAuth } from "../hooks/useAuth";

export interface PizzaIngredient {
    id?: string;
    name: string;
    emoji: string;
    color?: string;
    extraPrice?: number;
}

export interface PizzaFlavor {
    id?: string;
    name: string;
    emoji: string;
    color: string;
    description: string;
    extraPrice?: number;
    tier?: 1 | 2 | 3;
}

const emptyIngredient: PizzaIngredient = {
    name: "",
    emoji: "🧀",
    color: "#f39c12",
    extraPrice: 0,
};

const emptyFlavor: PizzaFlavor = {
    name: "",
    emoji: "🍕",
    color: "#c0392b",
    description: "",
    extraPrice: 0,
    tier: 1,
};

/* ─── Color presets ─── */
const COLOR_PRESETS = [
    "#c0392b", "#d4ac0d", "#27ae60", "#8b4513",
    "#2ecc71", "#f39c12", "#e74c3c", "#922b21",
    "#9b59b6", "#2980b9", "#1abc9c", "#e67e22",
];

const formatPriceInputDisplay = (val: number | string | undefined | null) => {
    if (val === undefined || val === null || val === "") return "";
    const raw = String(val).replace(/\D/g, "");
    if (!raw) return "";
    const num = parseInt(raw, 10);
    return isNaN(num) ? "" : num.toLocaleString("es-CO");
};

const parsePriceInputNumber = (valStr: string) => {
    const raw = valStr.replace(/\D/g, "");
    return raw ? parseInt(raw, 10) : 0;
};

interface FlavorDialogProps {
    open: boolean;
    onClose: () => void;
    initial: PizzaFlavor;
    onSubmit: (values: PizzaFlavor) => Promise<void>;
}

function FlavorDialog({ open, onClose, initial, onSubmit }: FlavorDialogProps) {
    const [values, setValues] = useState<PizzaFlavor>(initial);

    // Reset when initial changes
    useState(() => { setValues(initial); });

    const handleChange = <K extends keyof PizzaFlavor>(key: K, value: PizzaFlavor[K]) => {
        setValues((v) => ({ ...v, [key]: value }));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 4 } }}
            // Reset on open
            TransitionProps={{ onEnter: () => setValues(initial) }}
        >
            <DialogTitle sx={{ fontWeight: 800, fontSize: "1.2rem", pb: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {initial.id ? "✏️ Editar Sabor" : "🍕 Nuevo Sabor"}
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Stack spacing={3} mt={1}>
                    {/* Nombre y emoji */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Nombre"
                            value={values.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Emoji"
                            value={values.emoji}
                            onChange={(e) => handleChange("emoji", e.target.value)}
                            sx={{ width: 100 }}
                            inputProps={{ style: { fontSize: "1.5rem", textAlign: "center" } }}
                        />
                    </Stack>

                    {/* Descripción y Precio Adicional */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Descripción"
                            value={values.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Ej: Salsa de tomate, queso mozzarella y pepperoni"
                        />
                        <Stack spacing={2} sx={{ width: 140 }}>
                            <TextField
                                label="Precio Adicional"
                                type="text"
                                value={formatPriceInputDisplay(values.extraPrice)}
                                onChange={(e) => handleChange("extraPrice", parsePriceInputNumber(e.target.value))}
                                placeholder="0"
                                InputProps={{
                                    startAdornment: <Typography sx={{ mr: 1, color: "text.secondary", fontWeight: 700 }}>$</Typography>,
                                }}
                            />
                            <TextField
                                select
                                label="Nivel"
                                value={values.tier || 1}
                                onChange={(e) => handleChange("tier", Number(e.target.value) as 1 | 2 | 3)}
                                SelectProps={{ native: true }}
                            >
                                <option value={1}>1-2 sabores</option>
                                <option value={2}>3-4 sabores</option>
                                <option value={3}>5 sabores</option>
                            </TextField>
                        </Stack>
                    </Stack>

                    {/* Color */}
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                            Color de identificación
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {COLOR_PRESETS.map((c) => (
                                <Tooltip key={c} title={c}>
                                    <Box
                                        component="button"
                                        onClick={() => handleChange("color", c)}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            bgcolor: c,
                                            border: values.color === c ? "3px solid #000" : "3px solid transparent",
                                            cursor: "pointer",
                                            outline: "none",
                                            transition: "transform 0.15s",
                                            "&:hover": { transform: "scale(1.15)" },
                                        }}
                                    />
                                </Tooltip>
                            ))}
                            {/* Custom color input */}
                            <Tooltip title="Color personalizado">
                                <Box sx={{ position: "relative" }}>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={values.color}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("color", e.target.value)}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "50%",
                                            border: "3px solid #e2e8f0",
                                            cursor: "pointer",
                                            padding: 0,
                                            overflow: "hidden",
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        </Stack>
                        {/* Preview */}
                        <Stack direction="row" spacing={1.5} alignItems="center" mt={2}>
                            <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: values.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }}>
                                {values.emoji}
                            </Box>
                            <Box>
                                <Typography fontWeight={700}>{values.name || "Nombre del sabor"}</Typography>
                                <Typography variant="caption" color="text.secondary">{values.description || "Descripción"}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", fontWeight: 700 }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    disabled={!values.name.trim()}
                    onClick={() => onSubmit(values)}
                    sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}
                >
                    Guardar Sabor
                </Button>
            </DialogActions>
        </Dialog>
    );
}


interface IngredientDialogProps {
    open: boolean;
    onClose: () => void;
    initial: PizzaIngredient;
    onSubmit: (values: PizzaIngredient) => Promise<void>;
}

function IngredientDialog({ open, onClose, initial, onSubmit }: IngredientDialogProps) {
    const [values, setValues] = useState<PizzaIngredient>(initial);
    useState(() => { setValues(initial); });

    const handleChange = <K extends keyof PizzaIngredient>(key: K, value: PizzaIngredient[K]) => {
        setValues((v) => ({ ...v, [key]: value }));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }} TransitionProps={{ onEnter: () => setValues(initial) }}>
            <DialogTitle sx={{ fontWeight: 800, fontSize: "1.2rem", pb: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {initial.id ? "✏️ Editar Ingrediente" : "🧀 Nuevo Ingrediente"}
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Stack spacing={3} mt={1}>
                    <Stack direction="row" spacing={2}>
                        <TextField label="Nombre" value={values.name} onChange={(e) => handleChange("name", e.target.value)} fullWidth required />
                        <TextField label="Emoji" value={values.emoji} onChange={(e) => handleChange("emoji", e.target.value)} sx={{ width: 100 }} inputProps={{ style: { fontSize: "1.5rem", textAlign: "center" } }} />
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            label="Precio Adicional"
                            type="text"
                            value={formatPriceInputDisplay(values.extraPrice)}
                            onChange={(e) => handleChange("extraPrice", parsePriceInputNumber(e.target.value))}
                            placeholder="0"
                            fullWidth
                            InputProps={{
                                startAdornment: <Typography sx={{ mr: 1, color: "text.secondary", fontWeight: 700 }}>$</Typography>,
                            }}
                        />
                    </Stack>

                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>Color de identificación</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {COLOR_PRESETS.map((c) => (
                                <Tooltip key={c} title={c}>
                                    <Box component="button" onClick={() => handleChange("color", c)} sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: c, border: values.color === c ? "3px solid #000" : "3px solid transparent", cursor: "pointer", outline: "none", transition: "transform 0.15s", "&:hover": { transform: "scale(1.15)" } }} />
                                </Tooltip>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", fontWeight: 700 }}>Cancelar</Button>
                <Button variant="contained" size="large" disabled={!values.name.trim()} onClick={() => onSubmit(values)} sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>Guardar Ingrediente</Button>
            </DialogActions>
        </Dialog>
    );
}

/* ─── Main Page ─── */
export default function FlavorsPage() {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { canCreate, canEdit, canDelete } = useAuth();

    const [tabValue, setTabValue] = useState(0);
    const { data: ingredientsData, createItem: createIngredient, updateItem: updateIngredient, deleteItem: deleteIngredient } = useCollection<PizzaIngredient>(restaurantId!, "ingredients");
    const [openIng, setOpenIng] = useState(false);
    const [currentIng, setCurrentIng] = useState<PizzaIngredient | null>(null);

    const handleEditIng = (ing: PizzaIngredient) => { setCurrentIng(ing); setOpenIng(true); };
    const handleNewIng = () => { setCurrentIng({ ...emptyIngredient }); setOpenIng(true); };

    const { data, createItem, updateItem, deleteItem } =
        useCollection<PizzaFlavor>(restaurantId!, "flavors");

    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState<PizzaFlavor | null>(null);

    const [tierPrices, setTierPrices] = useState<Record<number, number>>({ 1: 0, 2: 3000, 3: 6000 });
    const [ingTierPrices, setIngTierPrices] = useState<Record<number, number>>({ 1: 0, 2: 3000, 3: 6000 });
    const [tierPriceEdit, setTierPriceEdit] = useState<{ open: boolean; tier: 1 | 2 | 3; price: number; isIngredientTier?: boolean }>({ open: false, tier: 1, price: 0, isIngredientTier: false });

    useEffect(() => {
        if (!restaurantId) return;
        const docRef = doc(db, "restaurants", restaurantId, "config", "tierPrices");
        const unsub = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
                const d = snap.data();
                setTierPrices({
                    1: d.tier1 ?? 0,
                    2: d.tier2 ?? 3000,
                    3: d.tier3 ?? 6000,
                });
                setIngTierPrices({
                    1: d.ingTier1 ?? 0,
                    2: d.ingTier2 ?? 3000,
                    3: d.ingTier3 ?? 6000,
                });
            }
        });
        return () => unsub();
    }, [restaurantId]);

    const saveTierPrice = async (tier: 1 | 2 | 3, price: number, isIngredientTier?: boolean) => {
        if (!restaurantId) return;
        const docRef = doc(db, "restaurants", restaurantId, "config", "tierPrices");
        const fieldKey = isIngredientTier ? `ingTier${tier}` : `tier${tier}`;
        await setDoc(docRef, { [fieldKey]: price }, { merge: true });
    };

    const handleEdit = (flavor: PizzaFlavor) => {
        setCurrent(flavor);
        setOpen(true);
    };

    const handleNew = (tier: 1 | 2 | 3 = 1) => {
        setCurrent({ ...emptyFlavor, tier });
        setOpen(true);
    };

    const tiersConfig = [
        { id: 1 as const, label: "1-2 sabores", color: "#15803d", bg: "rgba(22,163,74,0.04)" },
        { id: 2 as const, label: "3-4 sabores", color: "#c2410c", bg: "rgba(234,88,12,0.04)" },
        { id: 3 as const, label: "5 sabores", color: "#7c3aed", bg: "rgba(147,51,234,0.04)" },
    ];

    return (
        <>
            {/* HEADER */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                mb={3}
            >
                <Box>
                    <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: "-0.03em" }}>
                        🍕 Sabores e Ingredientes
                    </Typography>
                    <Typography color="text.secondary" variant="body1">
                        Gestiona los sabores de las pizzas y los ingredientes de Arma tu Pizza.
                    </Typography>
                </Box>

                {canCreate && (
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<AddIcon />}
                        onClick={() => tabValue === 0 ? handleNew(1) : handleNewIng()}
                        sx={{ borderRadius: 2.5, fontWeight: 800, px: 3 }}
                    >
                        {tabValue === 0 ? 'Nuevo Sabor' : 'Nuevo Ingrediente'}
                    </Button>
                )}
            </Stack>

            
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3, borderBottom: "1px solid #e2e8f0" }}>
                <Tab label="Sabores Predefinidos" sx={{ fontWeight: 800 }} />
                <Tab label="Ingredientes (Arma tu Pizza)" sx={{ fontWeight: 800 }} />
            </Tabs>

            {/* INFO BANNER */}
            {data.length === 0 && (
                <Box
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        bgcolor: "primary.50",
                        border: "1.5px dashed",
                        borderColor: "primary.main",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                        🍕 Aún no hay sabores registrados
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Agrega los sabores de pizza que estarán disponibles en la app de pedidos (Thomas Pizza).
                    </Typography>
                </Box>
            )}

            {tabValue === 0 && (
                <>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start" sx={{ pb: 4 }}>
                {tiersConfig.map((tierCfg) => {
                    const tierFlavors = data.filter((f) => (f.tier || 1) === tierCfg.id);
                    const currentTierPrice = tierPrices[tierCfg.id] ?? (tierCfg.id === 1 ? 0 : tierCfg.id === 2 ? 3000 : 6000);

                    return (
                        <Box
                            key={tierCfg.id}
                            sx={{
                                flex: 1,
                                width: "100%",
                                bgcolor: tierCfg.bg,
                                borderRadius: 4,
                                p: 2,
                                border: "1px solid",
                                borderColor: `${tierCfg.color}25`,
                            }}
                        >
                            {/* Column Header */}
                            <Stack spacing={1.5} mb={2} px={0.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography fontWeight={800} color={tierCfg.color} variant="h6">
                                        {tierCfg.label}
                                    </Typography>
                                    <Chip 
                                        label={`${tierFlavors.length} sabores`} 
                                        size="small" 
                                        sx={{ fontWeight: 800, bgcolor: "white", color: tierCfg.color, border: `1px solid ${tierCfg.color}25` }} 
                                    />
                                </Stack>

                                {/* Block Extra Price Box */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        bgcolor: "white",
                                        p: 1.5,
                                        borderRadius: 3,
                                        border: `1.5px solid ${tierCfg.color}35`,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
                                    }}
                                >
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", fontSize: "0.7rem", textTransform: "uppercase" }}>
                                            Precio extra del bloque
                                        </Typography>
                                        <Typography variant="body2" fontWeight={900} color={tierCfg.color} sx={{ fontSize: "0.95rem" }}>
                                            {currentTierPrice === 0 ? "Sin costo extra ($0)" : `+$${currentTierPrice.toLocaleString('es-CO')}`}
                                        </Typography>
                                    </Box>
                                    {canEdit && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AttachMoneyIcon fontSize="small" />}
                                            onClick={() => setTierPriceEdit({ open: true, tier: tierCfg.id, price: currentTierPrice })}
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 800,
                                                color: tierCfg.color,
                                                borderColor: `${tierCfg.color}50`,
                                                textTransform: "none",
                                                px: 1.5
                                            }}
                                        >
                                            Fijar Precio
                                        </Button>
                                    )}
                                </Box>
                            </Stack>

                            {/* Cards */}
                            <Stack spacing={2}>
                                {tierFlavors.map((flavor) => (
                                    <Card
                                        key={flavor.id}
                                        elevation={0}
                                        sx={{
                                            borderRadius: 3,
                                            border: "1px solid #e2e8f0",
                                            "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.06)" },
                                            transition: "box-shadow 0.2s"
                                        }}
                                    >
                                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                {/* Emoji circle */}
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: "50%",
                                                        bgcolor: flavor.color || "#c0392b",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "1.5rem",
                                                        flexShrink: 0,
                                                        boxShadow: `0 4px 10px ${flavor.color}40`
                                                    }}
                                                >
                                                    {flavor.emoji}
                                                </Box>

                                                {/* Details */}
                                                <Box flex={1} minWidth={0}>
                                                    <Typography fontWeight={800} noWrap sx={{ fontSize: "0.95rem" }}>
                                                        {flavor.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.2 }}>
                                                        {flavor.description || "Sin descripción"}
                                                    </Typography>
                                                </Box>

                                                {/* Actions */}
                                                <Stack spacing={0} flexShrink={0}>
                                                    {canEdit && (
                                                        <IconButton size="small" onClick={() => handleEdit(flavor)} sx={{ color: "text.secondary" }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    {canDelete && (
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                if (window.confirm(`¿Eliminar "${flavor.name}"?`))
                                                                    deleteItem(flavor.id!);
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}

                                {/* Add to this tier button */}
                                {canCreate && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleNew(tierCfg.id)}
                                        sx={{
                                            borderRadius: 3,
                                            color: tierCfg.color,
                                            borderColor: `${tierCfg.color}40`,
                                            borderStyle: "dashed",
                                            bgcolor: "white",
                                            fontWeight: 700,
                                            "&:hover": {
                                                bgcolor: `${tierCfg.color}08`,
                                                borderColor: tierCfg.color,
                                            }
                                        }}
                                    >
                                        Añadir a {tierCfg.label}
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>


        </>
            )}

            {tabValue === 1 && (
                <Box sx={{ pb: 4 }}>
                    {/* Tier Price Header for Ingredients (Arma tu Pizza) */}
                    <Box sx={{ mb: 4, p: 3, borderRadius: 4, bgcolor: "rgba(79,70,229,0.04)", border: "1px solid rgba(79,70,229,0.2)" }}>
                        <Typography variant="h6" fontWeight={800} color="#4f46e5" mb={0.5}>
                            💰 Precios por Cantidad de Ingredientes en "Arma tu Pizza"
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2.5}>
                            Fija el precio adicional que se cobrará al cliente al personalizar su pizza con 1-2, 3-4 o 5 ingredientes:
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                { tier: 1 as const, label: "1-2 ingredientes", color: "#15803d" },
                                { tier: 2 as const, label: "3-4 ingredientes", color: "#c2410c" },
                                { tier: 3 as const, label: "5 ingredientes", color: "#7c3aed" },
                            ].map((tierCfg) => {
                                const price = ingTierPrices[tierCfg.tier] ?? (tierCfg.tier === 1 ? 0 : tierCfg.tier === 2 ? 3000 : 6000);
                                return (
                                    <Grid size={{ xs: 12, md: 4 }} key={tierCfg.tier}>
                                        <Box
                                            sx={{
                                                bgcolor: "white",
                                                p: 2,
                                                borderRadius: 3,
                                                border: `1.5px solid ${tierCfg.color}35`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
                                            }}
                                        >
                                            <Box>
                                                <Typography fontWeight={800} color={tierCfg.color} variant="subtitle2">
                                                    {tierCfg.label}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={900} color={tierCfg.color} sx={{ fontSize: "1rem", mt: 0.5 }}>
                                                    {price === 0 ? "Sin costo extra ($0)" : `+$${price.toLocaleString('es-CO')}`}
                                                </Typography>
                                            </Box>
                                            {canEdit && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<AttachMoneyIcon fontSize="small" />}
                                                    onClick={() => setTierPriceEdit({ open: true, tier: tierCfg.tier, price, isIngredientTier: true })}
                                                    sx={{
                                                        borderRadius: 2,
                                                        fontWeight: 800,
                                                        color: tierCfg.color,
                                                        borderColor: `${tierCfg.color}50`,
                                                        textTransform: "none",
                                                        px: 1.5
                                                    }}
                                                >
                                                    Fijar Precio
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>

                    <Typography variant="h6" fontWeight={800} mb={2}>
                        🧀 Catálogo de Ingredientes Disponibles
                    </Typography>

                    <Grid container spacing={2}>
                        {ingredientsData.map((ing) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={ing.id}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.06)" }, transition: "box-shadow 0.2s" }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: ing.color || "#c0392b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", flexShrink: 0, boxShadow: `0 4px 10px ${ing.color}40` }}>
                                                {ing.emoji}
                                            </Box>
                                            <Box flex={1} minWidth={0}>
                                                 <Typography fontWeight={800} noWrap sx={{ fontSize: "0.95rem" }}>
                                                     {ing.name}
                                                 </Typography>
                                            </Box>
                                            <Stack spacing={0} flexShrink={0}>
                                                {canEdit && (
                                                    <IconButton size="small" onClick={() => handleEditIng(ing)} sx={{ color: "text.secondary" }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                {canDelete && (
                                                    <IconButton size="small" color="error" onClick={() => { if (window.confirm(`¿Eliminar "${ing.name}"?`)) deleteIngredient(ing.id!); }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {ingredientsData.length === 0 && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ p: 3, borderRadius: 3, bgcolor: "primary.50", border: "1.5px dashed", borderColor: "primary.main", textAlign: "center" }}>
                                    <Typography variant="h6" fontWeight={700} color="primary.main">🧀 Aún no hay ingredientes</Typography>
                                    <Typography variant="body2" color="text.secondary" mt={0.5}>Agrega ingredientes para la opción de "Arma tu pizza".</Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {/* DIALOG FOR TIER BLOCK PRICE */}
            <Dialog open={tierPriceEdit.open} onClose={() => setTierPriceEdit(prev => ({ ...prev, open: false }))} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 800, fontSize: "1.1rem", pb: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    💰 Precio Extra ({tierPriceEdit.isIngredientTier ? "Arma tu Pizza" : "Sabores"} - {tierPriceEdit.tier === 1 ? "1-2 ingredientes" : tierPriceEdit.tier === 2 ? "3-4 ingredientes" : "5 ingredientes"})
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Ingresa el precio fijo adicional que se cobrará al cliente al seleccionar esta cantidad de ingredientes en su pizza:
                    </Typography>
                    <TextField
                        label="Precio Extra del Bloque"
                        type="text"
                        value={formatPriceInputDisplay(tierPriceEdit.price)}
                        onChange={(e) => setTierPriceEdit(prev => ({ ...prev, price: parsePriceInputNumber(e.target.value) }))}
                        fullWidth
                        autoFocus
                        placeholder="0"
                        InputProps={{
                            startAdornment: <Typography sx={{ mr: 1, color: "text.secondary", fontWeight: 700 }}>$</Typography>,
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                    <Button onClick={() => setTierPriceEdit(prev => ({ ...prev, open: false }))} sx={{ color: "text.secondary", fontWeight: 700 }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={async () => {
                            await saveTierPrice(tierPriceEdit.tier, tierPriceEdit.price, tierPriceEdit.isIngredientTier);
                            setTierPriceEdit(prev => ({ ...prev, open: false }));
                        }}
                        sx={{ borderRadius: 2, px: 3, fontWeight: 800 }}
                    >
                        Guardar Precio
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DIALOGS */}
            <FlavorDialog
                open={open}
                onClose={() => setOpen(false)}
                initial={current ?? emptyFlavor}
                onSubmit={async (values) => {
                    if (current?.id) await updateItem(current.id, values);
                    else await createItem(values);
                    setOpen(false);
                }}
            />
            <IngredientDialog
                open={openIng}
                onClose={() => setOpenIng(false)}
                initial={currentIng ?? emptyIngredient}
                onSubmit={async (values) => {
                    if (currentIng?.id) await updateIngredient(currentIng.id, values);
                    else await createIngredient(values);
                    setOpenIng(false);
                }}
            />
        </>
    );
}
